import React, { useEffect, useState, useMemo } from "react";
import { useTable, useSortBy, usePagination } from "react-table";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import {
  Button,
  Dialog,
  DialogTitle,
  IconButton,
  InputAdornment,
  MenuItem,
  Select,
  TextField,
  Tooltip,
} from "@mui/material";
import { branches, getAdcUsers } from "./Utilities/api";
import IdleTimerRedirect from "../../components/Other/IdleTimerRedirect";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import BadgeOutlinedIcon from "@mui/icons-material/BadgeOutlined";
import PersonAddAlt1Icon from "@mui/icons-material/PersonAddAlt1";
import SearchIcon from "@mui/icons-material/Search";
import ArrowUpwardIcon from "@mui/icons-material/ArrowUpward";
import ArrowDownwardIcon from "@mui/icons-material/ArrowDownward";
import { PageHeader, PageShell } from "../../components/UI/Page";
import { LoadingState } from "../../components/UI/States";

const TEXT_INPUT_CLASS =
  "app-input w-full rounded-xl border px-3 py-2 text-sm";

// Field label sitting above its input.
const LABEL_CLASS = "flex flex-col gap-1.5 text-sm font-medium";

const UserPortal = () => {
  const [addUserModal, setAddModal] = useState(false);
  const [userDataFiltered, setUserDataFiltered] = useState(null);
  const [addSuccess, setAddSuccess] = useState(false);
  const [userData, setUserData] = useState([]);

  // New state for managing edit dialog
  const [editUser, setEditUser] = useState(null);
  const [editModal, setEditModal] = useState(false);

  useEffect(() => {
    getAdcUsers(setUserData);
  }, []);

  const [newUser, setNewUser] = useState({
    name: "",
    jobTitle: "",
    password: "",
    branch: "",
    login: "",
    operatorNumber: null,
  });

  const data = useMemo(
    () => userDataFiltered || userData,
    [userDataFiltered, userData]
  );

  const columns = useMemo(
    () => [
      {
        Header: "Operator #",
        accessor: "operatorNumber",
      },
      {
        Header: "Name",
        accessor: "name",
      },
      {
        Header: "Title",
        accessor: "jobTitle",
      },
      {
        Header: "Login",
        accessor: "login",
      },
      {
        Header: "Group",
        accessor: "adcGroup",
      },
      {
        Header: "Branch",
        accessor: "branch",
      },
      {
        Header: "Password",
        accessor: "password",
        Cell: ({ value }) => <PasswordCell value={value} />,
      },
      {
        Header: "Action",
        accessor: "action",
        Cell: ({ row }) => (
          <Button
            size="small"
            variant="outlined"
            onClick={() => {
              setEditUser(row.original);
              setEditModal(true);
            }}
          >
            Edit
          </Button>
        ),
      },
    ],
    []
  );

  const {
    getTableProps,
    getTableBodyProps,
    headerGroups,
    page,
    nextPage,
    previousPage,
    canNextPage,
    canPreviousPage,
    pageOptions,
    state: { pageIndex },
    prepareRow,
  } = useTable(
    {
      columns,
      data,
      initialState: { pageIndex: 0, pageSize: 50 },
    },
    useSortBy,
    usePagination
  );

  //Search Users by name
  const handleSearch = (e) => {
    const search = e.target.value.toLowerCase();

    if (search) {
      const filtered = userData.filter((user) => {
        return Object.values(user).some((value) =>
          value.toString().toLowerCase().includes(search)
        );
      });
      setUserDataFiltered(filtered);
    } else {
      setUserDataFiltered(userData);
    }
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setNewUser((prevUser) => ({ ...prevUser, [name]: value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    // Assuming your API endpoint is '/api/users'
    const response = await fetch("/api/profile/portal/addUser", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(newUser),
    });

    if (response.status === 200) {
      getAdcUsers(setUserData);
      setAddModal(false);
    }
  };
  const handleEditSubmit = async (e) => {
    e.preventDefault();

    // Assuming your API endpoint is '/api/profile/portal/editUser'
    const response = await fetch("/api/profile/portal/updateUser", {
      method: "POST",
      headers: {
        "Content-Type": "application/json",
      },
      body: JSON.stringify(editUser),
    });

    if (response.status === 200) {
      getAdcUsers(setUserData);
      setEditModal(false);
    }
  };

  return (
    <PageShell size="2xl">
      <IdleTimerRedirect time={10} route={"/"} />
      <PageHeader
        icon={BadgeOutlinedIcon}
        tone="slate"
        eyebrow="Admin"
        title="Big C User Portal"
        description="ADC portal accounts — operator numbers, logins, groups, and passwords."
        actions={
          userData.length > 0 && (
            <Button
              variant="contained"
              startIcon={<PersonAddAlt1Icon />}
              onClick={() => {
                setAddModal(true);
              }}
            >
              Add User
            </Button>
          )
        }
      />
      {userData.length > 0 ? (
        <div className="app-card overflow-hidden rounded-2xl border">
          <div className="flex flex-col gap-3 border-b app-divider px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5">
            <div>
              <h2 className="font-semibold">All Users</h2>
              <p className="app-muted text-xs">
                Double-click a row to add a user with the same title, branch,
                and group.
              </p>
            </div>
            <TextField
              size="small"
              type="text"
              placeholder="Search Users"
              onChange={handleSearch}
              className="w-full sm:w-72"
              inputProps={{ "aria-label": "Search Users" }}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon fontSize="small" />
                  </InputAdornment>
                ),
              }}
            />
          </div>

          <div className="overflow-x-auto">
            <table {...getTableProps()} className="app-table">
              <thead>
                {headerGroups.map((headerGroup) => (
                  <tr {...headerGroup.getHeaderGroupProps()}>
                    {headerGroup.headers.map((column) => (
                      <th
                        {...column.getHeaderProps(column.getSortByToggleProps())}
                        className="!px-4"
                      >
                        <span className="inline-flex items-center gap-1">
                          {column.render("Header")}
                          {column.isSorted &&
                            (column.isSortedDesc ? (
                              <ArrowDownwardIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <ArrowUpwardIcon sx={{ fontSize: 14 }} />
                            ))}
                        </span>
                      </th>
                    ))}
                  </tr>
                ))}
              </thead>
              <tbody {...getTableBodyProps()}>
                {page.map((row) => {
                  prepareRow(row);
                  return (
                    <tr
                      onDoubleClick={() => {
                        setNewUser({
                          name: "",
                          jobTitle: row.original.jobTitle,
                          password: "",
                          branch: row.original.branch,
                          login: "",
                          operatorNumber: null,
                          adcGroup: row.original.adcGroup,
                        });
                        setAddSuccess(false);
                        setAddModal(true);
                      }}
                      {...row.getRowProps()}
                    >
                      {row.cells.map((cell) => (
                        <td {...cell.getCellProps()} className="!px-4">
                          {cell.render("Cell")}
                        </td>
                      ))}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="flex items-center justify-between gap-2 border-t app-divider px-4 py-3 sm:px-5">
            <span className="app-muted text-sm tabular-nums">
              Page{" "}
              <strong>
                {pageIndex + 1} of {pageOptions.length}
              </strong>
            </span>
            <div className="flex gap-1">
              <Button
                size="small"
                onClick={() => previousPage()}
                disabled={!canPreviousPage}
              >
                Prev
              </Button>
              <Button
                size="small"
                onClick={() => nextPage()}
                disabled={!canNextPage}
              >
                Next
              </Button>
            </div>
          </div>
        </div>
      ) : (
        <LoadingState label="Loading users…" />
      )}

      {addUserModal && (
        <Dialog
          open={addUserModal}
          onClose={() => {
            setAddModal(false);
          }}
          fullWidth
          maxWidth={"sm"}
          key={addUserModal ? "open" : "closed"}
        >
          {!addSuccess ? (
            <>
              <DialogTitle>Add User</DialogTitle>
              <form
                className="grid grid-cols-1 gap-4 px-6 pb-6 sm:grid-cols-2"
                onSubmit={handleSubmit}
              >
                <label className={LABEL_CLASS}>
                  Name:
                  <input
                    type="text"
                    className={TEXT_INPUT_CLASS}
                    name="name"
                    value={newUser.name}
                    onChange={handleInputChange}
                  />
                </label>
                <label className={LABEL_CLASS}>
                  Operator Number:
                  <input
                    type="text"
                    className={TEXT_INPUT_CLASS}
                    name="operatorNumber"
                    value={newUser.operatorNumber}
                    onChange={handleInputChange}
                  />
                </label>
                <label className={LABEL_CLASS}>
                  Job Title:
                  <input
                    type="text"
                    className={TEXT_INPUT_CLASS}
                    name="jobTitle"
                    value={newUser.jobTitle}
                    onChange={handleInputChange}
                  />
                </label>
                <label className={LABEL_CLASS}>
                  ADC Login:
                  <input
                    type="text"
                    className={TEXT_INPUT_CLASS}
                    name="login"
                    value={newUser.login}
                    onChange={handleInputChange}
                  />
                </label>

                <label className={LABEL_CLASS}>
                  ADC Group:
                  <input
                    type="text"
                    className={TEXT_INPUT_CLASS}
                    name="adcGroup"
                    value={newUser.adcGroup}
                    onChange={handleInputChange}
                  />
                </label>

                <label className={LABEL_CLASS}>
                  Password:
                  <input
                    type="text"
                    className={TEXT_INPUT_CLASS}
                    name="password"
                    value={newUser.password}
                    onChange={handleInputChange}
                  />
                </label>
                <label className={`${LABEL_CLASS} sm:col-span-2`}>
                  Branch Location:
                  <Select
                    size="small"
                    value={newUser.branch}
                    onChange={handleInputChange}
                    className="w-full"
                    name="branch"
                  >
                    {branches.map((item) => {
                      return <MenuItem value={item}>{item}</MenuItem>;
                    })}
                  </Select>
                </label>
                <div className="flex justify-end border-t app-divider pt-4 sm:col-span-2">
                  <Button variant="contained" type="submit">
                    Add User
                  </Button>
                </div>
              </form>
            </>
          ) : (
            <div className="flex w-full flex-col items-center justify-center p-6 text-center">
              <CheckCircleIcon
                className="!text-emerald-600 dark:!text-emerald-400"
                sx={{ fontSize: 56 }}
              />
              <p className="mt-2 text-lg font-semibold">
                User Successfully Added!
              </p>

              <div className="mt-5 flex flex-wrap justify-center gap-2">
                <Button
                  variant="contained"
                  onClick={() => {
                    setAddSuccess(false);
                    setNewUser({
                      name: "",
                      jobTitle: "",
                      password: "",
                      branch: "",
                      login: "",
                      operatorNumber: null,
                      adcGroup: "",
                    });
                  }}
                >
                  Add Another
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => {
                    setAddSuccess(false);
                    setAddModal(false);
                    setNewUser({
                      name: "",
                      jobTitle: "",
                      password: "",
                      branch: "",
                      login: "",
                      operatorNumber: null,
                      adcGroup: "",
                    });
                  }}
                >
                  Close
                </Button>
              </div>
            </div>
          )}
        </Dialog>
      )}

      {editUser && (
        <Dialog
          open={editModal}
          onClose={() => {
            setEditModal(false);
          }}
          fullWidth
          maxWidth={"sm"}
          key={editModal ? "open" : "closed"}
        >
          <DialogTitle>Edit User</DialogTitle>
          <form
            className="grid grid-cols-1 gap-4 px-6 pb-6 sm:grid-cols-2"
            onSubmit={handleEditSubmit}
          >
            {/* Add input fields for editing user information */}
            <label className={LABEL_CLASS}>
              Operator Number:
              <input
                type="text"
                className={TEXT_INPUT_CLASS}
                name="operatorNumber"
                value={editUser.operatorNumber}
                onChange={(e) =>
                  setEditUser((prevUser) => ({
                    ...prevUser,
                    operatorNumber: e.target.value,
                  }))
                }
              />
            </label>

            <label className={LABEL_CLASS}>
              Name:
              <input
                type="text"
                className={TEXT_INPUT_CLASS}
                name="name"
                value={editUser.name}
                onChange={(e) =>
                  setEditUser((prevUser) => ({
                    ...prevUser,
                    name: e.target.value,
                  }))
                }
              />
            </label>

            <label className={LABEL_CLASS}>
              Title:
              <input
                type="text"
                className={TEXT_INPUT_CLASS}
                name="jobTitle"
                value={editUser.jobTitle}
                onChange={(e) =>
                  setEditUser((prevUser) => ({
                    ...prevUser,
                    jobTitle: e.target.value,
                  }))
                }
              />
            </label>

            <label className={LABEL_CLASS}>
              Login:
              <input
                type="text"
                className={TEXT_INPUT_CLASS}
                name="login"
                value={editUser.login}
                onChange={(e) =>
                  setEditUser((prevUser) => ({
                    ...prevUser,
                    login: e.target.value,
                  }))
                }
              />
            </label>

            <label className={LABEL_CLASS}>
              ADC Group:
              <input
                type="text"
                className={TEXT_INPUT_CLASS}
                name="adcGroup"
                value={editUser.adcGroup}
                onChange={(e) =>
                  setEditUser((prevUser) => ({
                    ...prevUser,
                    adcGroup: e.target.value,
                  }))
                }
              />
            </label>

            <label className={LABEL_CLASS}>
              Branch:
              <Select
                size="small"
                value={editUser.branch}
                onChange={(e) =>
                  setEditUser((prevUser) => ({
                    ...prevUser,
                    branch: e.target.value,
                  }))
                }
                className="w-full"
                name="branch"
              >
                {branches.map((item) => {
                  return <MenuItem value={item}>{item}</MenuItem>;
                })}
              </Select>
            </label>

            <label className={`${LABEL_CLASS} sm:col-span-2`}>
              Password:
              <input
                type="text"
                className={TEXT_INPUT_CLASS}
                name="password"
                value={editUser.password}
                onChange={(e) =>
                  setEditUser((prevUser) => ({
                    ...prevUser,
                    password: e.target.value,
                  }))
                }
              />
            </label>

            <div className="flex justify-end border-t app-divider pt-4 sm:col-span-2">
              <Button variant="contained" type="submit">
                Save Changes
              </Button>
            </div>
          </form>
        </Dialog>
      )}
    </PageShell>
  );
};

// Custom cell component for rendering password with show/hide functionality
const PasswordCell = ({ value }) => {
  const [showPassword, setShowPassword] = useState(false);

  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
  };

  return (
    <div className="flex items-center gap-1">
      <span className="min-w-[5.5rem] font-mono text-xs">
        {showPassword ? value : "********"}
      </span>
      <Tooltip title="Copy Password">
        <IconButton
          size="small"
          onClick={() => copyToClipboard(value)}
          aria-label="Copy Password"
        >
          <ContentCopyIcon fontSize="small" />
        </IconButton>
      </Tooltip>
      <Tooltip title="Show/Hide Password">
        <IconButton
          size="small"
          onClick={togglePasswordVisibility}
          aria-label="Show/Hide Password"
        >
          {showPassword ? (
            <VisibilityOffIcon fontSize="small" />
          ) : (
            <VisibilityIcon fontSize="small" />
          )}
        </IconButton>
      </Tooltip>
    </div>
  );
};

export default UserPortal;
